import { BellRing, Mail, Siren } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAlertasAdminControllerListar } from '@/api/generated/admin-alertas/admin-alertas';
import type { AlertasAdminControllerListarParams } from '@/api/generated/model';
import { DestinatariosTab } from '@/features/admin/alertas/components/DestinatariosTab';
import { DetalheDoAlertaDialogo } from '@/features/admin/alertas/components/DetalheDoAlertaDialogo';
import {
  CLASSE_SEVERIDADE,
  dataHora,
  FILTRO_PADRAO_DE_SEVERIDADE,
  ORIGENS,
  ROTULO_ORIGEM,
  ROTULO_SEVERIDADE,
  ROTULO_STATUS,
  SEVERIDADES,
  STATUS,
} from '@/features/admin/alertas/rotulos';
import { fimDoDiaISO, inicioDoDiaISO } from '@/features/admin/auditoria/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { ROTAS, rotaDoAlerta } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

const TODOS = 'todos';
/** Atualiza sozinha: quem abre esta tela num incidente quer ver o contador subir. */
const INTERVALO_DE_ATUALIZACAO_MS = 30_000;

/**
 * Administração → Alertas: erros da API, do navegador e das integrações
 * externas, agrupados (o mesmo erro repetido é UM alerta com contador).
 *
 * Abre filtrada em erro + crítico e em aberto — os avisos (4xx) são muitos e
 * quase sempre uso normal. `/admin/alertas/:id` (o link do e-mail) abre a
 * tela já com o detalhe daquele alerta.
 */
export function AlertasPage() {
  const { pode } = usePermissoes();
  const navigate = useNavigate();
  const { id: alertaAberto } = useParams<{ id?: string }>();

  const [aba, setAba] = useState<'alertas' | 'destinatarios'>('alertas');
  const [pagina, setPagina] = useState(1);
  const [severidade, setSeveridade] = useState<string>(FILTRO_PADRAO_DE_SEVERIDADE);
  const [status, setStatus] = useState<string>('ABERTO');
  const [origem, setOrigem] = useState<string>(TODOS);
  const [busca, setBusca] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');

  const parametros: AlertasAdminControllerListarParams = {
    pagina,
    porPagina: 20,
    ordenarPor: 'ultimaEm',
    ordem: 'desc',
    ...(severidade !== TODOS ? { severidade } : {}),
    ...(status !== TODOS ? { status } : {}),
    ...(origem !== TODOS ? { origem } : {}),
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(de ? { de: inicioDoDiaISO(de) } : {}),
    ...(ate ? { ate: fimDoDiaISO(ate) } : {}),
  };
  const { data, isPending, isError, refetch } = useAlertasAdminControllerListar(parametros, {
    query: { refetchInterval: INTERVALO_DE_ATUALIZACAO_MS },
  });

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  const podeVerDestinatarios = pode('admin_alertas_destinatarios.visualizar');

  return (
    <div className="space-y-6">
      <Tabs value={aba} onValueChange={(v) => setAba(v as 'alertas' | 'destinatarios')}>
        <TabsList>
          <TabsTrigger value="alertas">
            <BellRing className="w-4 h-4" />
            Alertas
          </TabsTrigger>
          {podeVerDestinatarios && (
            <TabsTrigger value="destinatarios">
              <Mail className="w-4 h-4" />
              Destinatários de e-mail
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="alertas" className="mt-4 space-y-6">
          <div className="flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3">
            <Input
              value={busca}
              onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
              placeholder="Buscar por título, mensagem, rota ou código..."
              aria-label="Buscar alertas"
              className="lg:w-80"
            />

            <Select value={severidade} onValueChange={(v) => aoFiltrar(() => setSeveridade(v))}>
              <SelectTrigger className="lg:w-48" aria-label="Filtrar por severidade">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FILTRO_PADRAO_DE_SEVERIDADE}>Erros e críticos</SelectItem>
                <SelectItem value={TODOS}>Todas as severidades</SelectItem>
                {SEVERIDADES.map((valor) => (
                  <SelectItem key={valor} value={valor}>
                    Só {ROTULO_SEVERIDADE[valor].toLowerCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={status} onValueChange={(v) => aoFiltrar(() => setStatus(v))}>
              <SelectTrigger className="lg:w-40" aria-label="Filtrar por status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos os status</SelectItem>
                {STATUS.map((valor) => (
                  <SelectItem key={valor} value={valor}>
                    {ROTULO_STATUS[valor]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={origem} onValueChange={(v) => aoFiltrar(() => setOrigem(v))}>
              <SelectTrigger className="lg:w-44" aria-label="Filtrar por origem">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todas as origens</SelectItem>
                {ORIGENS.map((valor) => (
                  <SelectItem key={valor} value={valor}>
                    {ROTULO_ORIGEM[valor]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={de}
                onChange={(e) => aoFiltrar(() => setDe(e.target.value))}
                aria-label="Data inicial"
                className="w-40"
              />
              <span className="text-muted-foreground text-sm">até</span>
              <Input
                type="date"
                value={ate}
                onChange={(e) => aoFiltrar(() => setAte(e.target.value))}
                aria-label="Data final"
                className="w-40"
              />
            </div>
          </div>

          <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
              <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">Alertas de erro</h3>
            </div>

            {isPending ? (
              <div className="p-6 space-y-3">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 rounded-md" />
                ))}
              </div>
            ) : isError || !data ? (
              <EstadoErro aoTentarDeNovo={() => void refetch()} />
            ) : data.itens.length === 0 ? (
              <EstadoVazio icone={Siren} titulo="Nenhum alerta" descricao="Nenhum erro bate com os filtros escolhidos." />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Severidade</TableHead>
                        <TableHead>Alerta</TableHead>
                        <TableHead>Origem</TableHead>
                        <TableHead className="text-right">Ocorrências</TableHead>
                        <TableHead>Última vez</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.itens.map((alerta) => (
                        <TableRow
                          key={alerta.id}
                          className="cursor-pointer"
                          onClick={() => navigate(rotaDoAlerta(alerta.id))}
                        >
                          <TableCell>
                            <Badge variant="outline" className={CLASSE_SEVERIDADE[alerta.severidade]}>
                              {ROTULO_SEVERIDADE[alerta.severidade]}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-md">
                            <button
                              type="button"
                              className="text-left font-medium text-foreground hover:underline"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(rotaDoAlerta(alerta.id));
                              }}
                            >
                              {alerta.titulo}
                            </button>
                            <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
                              {alerta.codigo}
                              {alerta.rota ? ` · ${alerta.metodo ?? ''} ${alerta.rota}` : ''}
                            </p>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{ROTULO_ORIGEM[alerta.origem]}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {alerta.ocorrencias.toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {dataHora(alerta.ultimaEm)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{ROTULO_STATUS[alerta.status]}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <Paginacao
                  pagina={data.pagina}
                  totalPaginas={data.totalPaginas}
                  total={data.total}
                  porPagina={data.porPagina}
                  aoMudarPagina={setPagina}
                />
              </>
            )}
          </div>
        </TabsContent>

        {podeVerDestinatarios && (
          <TabsContent value="destinatarios" className="mt-4">
            <DestinatariosTab />
          </TabsContent>
        )}
      </Tabs>

      {alertaAberto && (
        <DetalheDoAlertaDialogo alertaId={alertaAberto} aoFechar={() => navigate(ROTAS.adminAlertas)} />
      )}
    </div>
  );
}
